import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import { FC, ReactNode, useId, useRef, useState } from 'react';
import { Control, FieldPath, useWatch } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, SearchIcon } from '../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import Button from '../../../../../../components/button/index';
import {
  ComboboxButton,
  ComboboxOptions,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../../../../../../components/combobox/index';
import Loader from '../../../../../../components/loader/index';
import { Tooltip } from '../../../../../../components/tooltip/index';
import { useDebounce } from '../../../../../../hooks/index';
import { useInfiniteStakeholders } from '../../../../../../hooks/stakeholder/use-infinite-stakeholders';
import {
  Stakeholder,
  StakeholderOrder,
  stakeholderSingularTypeTitle,
} from '../../../../../../types/stakeholder.types';
import { QuickSignFormSchema } from '../../../validation/quick-sign.validation';

export type QuickSignStakeholderValue = {
  email?: string;
  fullName: string;
  id: string;
};

export type StakeholdersItemsListProps = {
  comboboxName: FieldPath<QuickSignFormSchema>;
  control: Control<QuickSignFormSchema>;
  data: Stakeholder[];
  fetchNextPage: (options?: FetchNextPageOptions) => Promise<
    InfiniteQueryObserverResult<
      InfiniteData<
        {
          stakeholders: Stakeholder[];
          totalPages: number;
        },
        unknown
      >,
      Error
    >
  >;
  hasNextPage: boolean;
  hasSearchValue: boolean;
  isLoading: boolean;
  scrollableTargetId: string;
};

const StakeholdersItemsList: FC<StakeholdersItemsListProps> = ({
  comboboxName,
  control,
  data,
  fetchNextPage,
  hasNextPage,
  hasSearchValue,
  isLoading,
  scrollableTargetId,
}) => {
  const navigate = useNavigate();

  return (
    <div className="flex w-full flex-col gap-2">
      <InfiniteScroll
        className="flex w-full flex-col gap-2"
        dataLength={data.length || 0}
        hasMore={hasNextPage}
        loader={<Loader iconClassName="size-8" />}
        next={fetchNextPage}
        scrollableTarget={scrollableTargetId}
        style={{ overflow: 'visible' }}
      >
        {data.length > 0 ? (
          data.map(({ email, fullName, id, type }, index) => (
            <FormComboboxOption
              className="w-full"
              control={control}
              key={`${id}_${index}`}
              name={comboboxName}
              value={
                {
                  id,
                  fullName,
                  ...(email != null && email !== '' && { email }),
                } satisfies QuickSignStakeholderValue
              }
            >
              <div className="flex w-full flex-col gap-[1px]">
                <Tooltip content={fullName}>
                  <span className="truncate text-sm text-gray-700">{fullName}</span>
                </Tooltip>
                <span className="truncate text-label-md italic text-gray-500">
                  <AppFormattedMessage id={stakeholderSingularTypeTitle[type]} />
                </span>
              </div>
            </FormComboboxOption>
          ))
        ) : isLoading ? (
          <span className="py-[6px] text-sm font-normal text-gray-700">
            <AppFormattedMessage id="LOADING" />
          </span>
        ) : (
          <span className="py-[6px] text-sm font-normal text-gray-700">
            {hasSearchValue ? (
              <AppFormattedMessage id="NO_SHAREHOLDER_FOUND" />
            ) : (
              <AppFormattedMessage id="NO_RELEVANT_STAKEHOLDERS_FOR_EVENT" />
            )}
          </span>
        )}
      </InfiniteScroll>

      <Button
        className="w-full justify-start text-nowrap rounded bg-brand-25 px-2 py-[6px] text-start text-sm font-[450] text-brand-700"
        onClick={() => navigate('/stakeholders?openAddModal=true')}
        styleType="NONE"
      >
        + <AppFormattedMessage id="ADD_NEW_SHAREHOLDER" />
      </Button>
    </div>
  );
};

export type StakeholdersComboboxProps = {
  className?: string;
  companyId: string;
  control: Control<QuickSignFormSchema>;
  excludeStakeholderIds?: string[];
  inputWrapperClassName?: string;
  name: FieldPath<QuickSignFormSchema>;
  placeholder?: ReactNode;
};

export const StakeholdersCombobox: FC<StakeholdersComboboxProps> = ({
  className,
  companyId,
  control,
  excludeStakeholderIds,
  inputWrapperClassName = 'min-h-[56px]',
  name,
  placeholder,
}) => {
  const scrollableTargetId = useId();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { debounceCallback } = useDebounce<string>({
    callback: (value) => {
      setDebouncedSearch(value);
    },
  });

  const { stakeholders, fetchNextPage, hasNextPage, isLoading } = useInfiniteStakeholders({
    search: debouncedSearch || '',
    companyId,
    sortOrder: StakeholderOrder.ASC,
  });

  const filteredStakeholders = excludeStakeholderIds?.length
    ? stakeholders.filter(({ id }) => !excludeStakeholderIds.includes(id))
    : stakeholders;

  const searchInputRef = useRef<HTMLInputElement>(null);
  const isCompactInput = inputWrapperClassName.includes('h-10');
  const selectedStakeholder = useWatch({ control, name }) as
    | null
    | QuickSignStakeholderValue
    | undefined;
  const resolvedPlaceholder =
    selectedStakeholder?.id != null
      ? undefined
      : (placeholder ?? <AppFormattedMessage id="CHOOSE" />);

  return (
    <FormCombobox
      className={twMerge('relative min-w-0 flex-1', className)}
      control={control}
      name={name}
      onFocus={() => {
        setSearch('');
        setDebouncedSearch('');
      }}
      onSelect={() => {
        setSearch('');
        setDebouncedSearch('');
      }}
    >
      <div className="relative">
        <FormComboboxInput
          control={control}
          customValue={(value) => (value as null | QuickSignStakeholderValue)?.fullName ?? ''}
          icon={
            <ChevronDownIcon
              className={twMerge('h-4 w-4 text-gray-500', !isCompactInput && 'mt-3')}
            />
          }
          name={name}
          placeholder={resolvedPlaceholder}
          readOnly
          wrapperClassName={inputWrapperClassName}
        />
        <ComboboxButton className="absolute left-0 top-0 z-1 h-full w-full" />
      </div>
      <ComboboxOptions className="w-full overflow-x-hidden" id={scrollableTargetId}>
        <div
          className="relative flex w-full items-center"
          onClick={() => searchInputRef.current?.focus()}
        >
          <SearchIcon className="absolute ml-[6px]" />
          <input
            className="w-full rounded border-[2px] border-transparent bg-gray-50 p-[6px] pl-8 text-gray-700 outline-none placeholder:text-gray-400 focus:border-brand-700 focus:bg-white"
            onChange={({ target: { value } }) => {
              setSearch(value);
              debounceCallback(value);
            }}
            ref={searchInputRef}
            value={search}
          />
        </div>
        <StakeholdersItemsList
          comboboxName={name}
          control={control}
          data={filteredStakeholders}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          hasSearchValue={!!search.length}
          isLoading={isLoading}
          scrollableTargetId={scrollableTargetId}
        />
      </ComboboxOptions>
    </FormCombobox>
  );
};
